import { logout, useJwtToken } from "@website/hooks/auth-manager";
import { MaterialIcon } from "@website/icons/material";
import { Avatar } from "@website/ui/avatar";
import { Badge } from "@website/ui/badge";
import { Sheet } from "@website/ui/sheet";
import { Skeleton } from "@website/ui/skeleton";
import { isChannel } from "@website/utils/channel/helpers";
import { numberConvert } from "@website/utils/number-convert";
import { useSnappPro } from "@website/widget/header/hooks/use-snapp-pro";
import { useCallback } from "react";
import profileImage from "../../assets/avatar.svg";
import { ProfileMenuItem } from "./components/profile-menu-item";
import { useGetBalance } from "./services/get-balance-service";

interface Properties {
  isOpen: boolean;
  close: () => void;
}

export function ProfileMenu({ isOpen, close }: Properties) {
  const { name: fullName, phoneNumber } = useJwtToken();
  const { hasSnappPro, snappProIcon, snappProTitle, snappProSubTitle } =
    useSnappPro();
  const {
    balance: { balance },
    isLoadingGetBalance
  } = useGetBalance();

  const onClickHandler = useCallback((pathname: string) => {
    if (typeof window !== "undefined") {
      window.location.href = pathname;
    }
  }, []);

  // eslint-disable-next-line unicorn/consistent-function-scoping
  function onLogout() {
    logout();
    window.location.href = "/";
  }

  const isTeleSalesChannel = isChannel(["telesale"]);
  const profileList = [
    ...(hasSnappPro
      ? [
          {
            label: snappProTitle,
            subTitle: snappProSubTitle,
            value: <Badge content="فعال" color="success" />,
            icon: <img src={snappProIcon} alt="snapp-pro" />
          }
        ]
      : []),
    ...(isTeleSalesChannel
      ? []
      : [
          {
            label: "کیف پول",
            value: isLoadingGetBalance ? (
              <Skeleton width={100} height={20} />
            ) : (
              `${numberConvert(((balance || 0) / 10).toLocaleString(), {
                locale: "fa"
              })} تومان`
            ),
            icon: (
              <MaterialIcon name="account_balance_wallet" variant="outlined" />
            ),
            onClick: () => onClickHandler("/profile/wallet")
          }
        ]),
    {
      label: "لیست مسافران",
      icon: <MaterialIcon name="people" variant="outlined" />,
      onClick: () => onClickHandler("/profile/passengers-list")
    },
    ...(isTeleSalesChannel
      ? []
      : [
          {
            label: "لیست تراکنش ها",
            value: "",
            icon: <MaterialIcon name="history" variant="outlined" />,
            onClick: () => onClickHandler("/profile/transactions")
          }
        ]),
    {
      label: "خروج",
      icon: <MaterialIcon name="logout" />,
      onClick: onLogout
    }
  ];

  return (
    <Sheet
      onClose={close}
      open={isOpen}
      xGap="16px"
      xPosition="end"
      yPosition="end"
    >
      <div className="min-w-[312px] py-3">
        <a
          href="/profile/information"
          className="flex items-center gap-4 px-6 py-2"
        >
          <Avatar
            src={profileImage.src}
            className="size-12 max-h-12 min-w-12"
            size="large"
            type="image"
            variant="circular"
          />
          <div>
            <div className="text-subtitle-2 text-on-surface-high-emphasis">
              {fullName || "نام کاربر"}
            </div>
            <p className="text-extra-caption text-on-surface-high-emphasis">
              {numberConvert(phoneNumber?.replaceAll?.("+98", "0"), {
                locale: "fa"
              })}
            </p>
          </div>
          <MaterialIcon
            name="chevron_left"
            onClick={close}
            className="mr-auto"
          />
        </a>
        {profileList.map((item) => (
          <ProfileMenuItem key={item.label} {...item} />
        ))}
      </div>
    </Sheet>
  );
}
